import { useMemo, useCallback, useRef, useEffect } from 'react';
import { UserProfile, FinancialConfig, Material, ProductTemplate, QuoteFormState, QuoteProductItem, ProfileStep } from '../types';
import { CATEGORIES } from '../constants/categories';
import { getEffectiveTaxRates } from '@r-max/shared';

export interface UseQuotePricingParams {
  formData: QuoteFormState;
  setFormData: React.Dispatch<React.SetStateAction<QuoteFormState>>;
  user: UserProfile;
  config: FinancialConfig;
  materials: Material[];
  recipes: ProductTemplate[];
  onOpenProfile?: (step?: ProfileStep) => void;
  onOpenCosts?: () => void;
  setAiExplanation: (ex: string) => void;
  setBudgetReady: (ready: boolean) => void;
}

export interface ProductivityBreakEven {
  unitVal: number;
  unitLabel: string;
  unitLabelPlural: string;
  vhb: number;
  minItemsPerHour: number;
  maxTimeFormatted: string;
  dynamicHourlyRevenue: number;
  netSpreadPerHour: number;
}

export interface UseQuotePricingReturn {
  costPrice: number;
  finalPrice: number;
  effectiveMargin: number;
  effectiveProfit: number;
  displayMargin: number;
  hiddenMarkupTotal: number;
  useOperationalTax: boolean;
  hasMathError: boolean;
  productivityBreakEven: ProductivityBreakEven | null;
  totalInputCredits?: number;
  b2bCreditTransferred?: number;
  effectiveClientCostB2B?: number;
  tcuBenchmarkApplied?: boolean;
  tcuProcessCitation?: string;
  isReformActive?: boolean;
  riskAlert: {
    type: string;
    title: string;
    message: string;
    actionLabel: string;
    action: () => void;
  } | null;
  loadRecipe: (recipeId: string) => void;
  buildSnapshotProducts: (products: QuoteProductItem[]) => QuoteProductItem[];
  isServiceNicheActive: (categoryIndex: number) => boolean;
}

export function useQuotePricing({
  formData,
  setFormData,
  user,
  config,
  materials,
  recipes,
  onOpenProfile,
  onOpenCosts,
  setAiExplanation,
  setBudgetReady
}: UseQuotePricingParams): UseQuotePricingReturn {

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const isServiceNicheActive = useCallback((categoryIndex: number): boolean =>
    CATEGORIES.find(c => c.id === categoryIndex)?.isServiceNiche ?? false, []);

  const riskAlert = useMemo(() => {
    const missingFinancial = config.proLaboreGoal <= 0 || config.monthlyFixedCost <= 0;
    const missingItems = materials.length === 0;

    if (missingFinancial) {
      return {
        type: 'financial',
        title: 'Cálculo de Risco',
        message: 'Atenção: Seus custos fixos não estão configurados. Este cálculo pode não refletir seu lucro real e representar um risco de prejuízo invisível.',
        actionLabel: 'Configurar Custos Agora',
        action: () => onOpenProfile && onOpenProfile('financial')
      };
    }

    if (missingItems) {
      return {
        type: 'items',
        title: 'Potencialize sua Margem',
        message: 'Você ainda não tem itens cadastrados no seu Centro de Custos. Use dados reais para orçamentos mais precisos.',
        actionLabel: 'Ir para Centro de Custos',
        action: () => onOpenCosts && onOpenCosts()
      };
    }

    return null;
  }, [config.proLaboreGoal, config.monthlyFixedCost, materials, onOpenProfile, onOpenCosts]);

  const pricing = useMemo(() => {
    const vhb = config.baseHourValue;
    const isBeginner = user.actingType === 'Novo na área';
    const riskT = isBeginner ? 1.25 : 1.0;
    const riskM = isBeginner ? 1.15 : 1.0;

    const totalMinutes = (formData.timeHours * 60) + formData.timeMinutes;
    const timeHours = Math.round(((totalMinutes / 60) * riskT) * 100) / 100;

    const pCost = (formData.products || []).reduce((acc: number, p: QuoteProductItem) => acc + (Number(p.total) || 0), 0);
    const sCost = (formData.services || []).reduce((acc: number, s: { name: string; value: number }) => acc + (Number(s.value) || 0), 0);
    
    // Custo real da mão de obra da empresa (Tempo de execução x Valor da Hora Base)
    // Se houver serviços cadastrados e o tempo for 0, considera ao menos 1h como baseline
    const effectiveHours = (formData.services || []).length > 0 && timeHours === 0 ? 1 : timeHours;
    const labor = Math.max(0, effectiveHours * vhb);
    const materialBase = (formData.products || []).length > 0 ? pCost : (Number(formData.materialCost) || 0);
    
    const costs = Math.max(0, (materialBase * riskM) + (Number(formData.extraCosts) || 0));

    const isSimples = user.taxRegime === 'Simples Nacional';
    const taxRate = isSimples ? (user.taxPercentage || 0) / 100 : 0;
    const paymentFeeRate = (config.paymentFeePercentage || 0) / 100;
    const commissionRate = (config.defaultCommissionPercentage || 0) / 100;
    const marginComponent = Number(formData.profitMarginInput || 0) / 100;

    // Apuração de Créditos de Insumos (CBS + IBS)
    const totalInputCredits = (formData.products || []).reduce((acc: number, p: QuoteProductItem) => {
      const cbs = Number(p.cbsCredit || 0);
      const ibs = Number(p.ibsCredit || 0);
      return acc + cbs + ibs;
    }, 0);

    const isReformMode = user.taxReformRegime === 'simples_b2b_reform' || user.taxReformRegime === 'regime_geral';

    let cp: number;
    let price: number;
    let b2bCreditTransferred = 0;
    let hasMathError = false;

    if (isReformMode) {
      // 1. Modelo Reforma Tributária (IVA Dual / Imposto Por Fora)
      const reformRates = getEffectiveTaxRates({ favoredRegimeKey: user.favoredRegimeCategory as any });
      const reformTaxRate = reformRates.totalRate / 100;

      // Abate créditos de insumos do custo base de materiais
      const netMaterialBase = Math.max(0, materialBase - totalInputCredits);
      const netCosts = Math.max(0, (netMaterialBase * riskM) + (Number(formData.extraCosts) || 0));
      const totalNetCosts = labor + netCosts;

      const operationalDeductions = marginComponent + paymentFeeRate + commissionRate;
      hasMathError = operationalDeductions >= 1;

      const safeOpDenominator = (1 - (paymentFeeRate + commissionRate)) <= 0 ? 0.01 : (1 - (paymentFeeRate + commissionRate));
      cp = Math.max(0, totalNetCosts / safeOpDenominator);

      const marginDenominator = (1 - operationalDeductions) <= 0 ? 0.01 : (1 - operationalDeductions);
      const netSellingPrice = totalNetCosts / marginDenominator;

      const reformTaxVal = netSellingPrice * reformTaxRate;
      const reformGrossFinal = netSellingPrice + reformTaxVal;
      const calculatedMarkupPrice = Math.max(reformGrossFinal, sCost + pCost);

      price = Math.max(0, Math.ceil(calculatedMarkupPrice / 5) * 5);
      b2bCreditTransferred = Number(reformTaxVal.toFixed(2));
    } else {
      // 2. Modelo Tradicional (Simples Nacional / Por Dentro / Divisor Clássico)
      const totalDeductions = marginComponent + taxRate + paymentFeeRate + commissionRate;
      hasMathError = totalDeductions >= 1;

      const calculateCostWithoutMargin = () => {
        const totalCosts = labor + costs;
        const safeDenominator = (1 - (taxRate + paymentFeeRate + commissionRate)) <= 0 ? 0.01 : (1 - (taxRate + paymentFeeRate + commissionRate));
        return totalCosts / safeDenominator;
      };

      const getPrice = (m: number) => {
        const totalCosts = labor + costs;
        const denominator = 1 - (m + taxRate + paymentFeeRate + commissionRate);
        const safeDenominator = denominator <= 0 ? 0.01 : denominator;
        const calculatedMarkupPrice = totalCosts / safeDenominator;
        return Math.max(calculatedMarkupPrice, sCost + pCost);
      };

      cp = Math.max(0, calculateCostWithoutMargin());
      const priceVal = getPrice(marginComponent);
      price = Math.max(0, Math.ceil(priceVal / 5) * 5);
    }

    const hiddenPCost = (formData.products || []).reduce((acc: number, p: QuoteProductItem) => acc + (p.isMarkupHidden ? (Number(p.total) || 0) : 0), 0);
    const denominator = isReformMode 
      ? (1 - (marginComponent + paymentFeeRate + commissionRate)) 
      : (1 - (marginComponent + taxRate + paymentFeeRate + commissionRate));
    const safeDenominator = denominator <= 0 ? 0.01 : denominator;
    
    const hiddenMarkupTotal = (hiddenPCost / safeDenominator) * marginComponent;
    const useOperationalTax = labor === 0 && hiddenMarkupTotal > 0;
    
    const effectiveClientCostB2B = Math.max(0, price - b2bCreditTransferred);

    return { 
      costPrice: cp, 
      finalPrice: price, 
      hiddenMarkupTotal, 
      useOperationalTax, 
      hasMathError,
      totalInputCredits,
      b2bCreditTransferred,
      effectiveClientCostB2B,
      isReformActive: isReformMode,
      tcuBenchmarkApplied: isReformMode,
      tcuProcessCitation: isReformMode ? 'TCU TC 006.529/2026-7 (CBS 11,3%)' : undefined
    };
  }, [
    config.baseHourValue, 
    user.actingType, 
    formData, 
    config.paymentFeePercentage, 
    config.defaultCommissionPercentage, 
    user.taxRegime, 
    user.taxPercentage,
    user.taxReformRegime,
    user.favoredRegimeCategory
  ]);

  const { 
    costPrice, 
    finalPrice: calculatedPrice, 
    hiddenMarkupTotal, 
    useOperationalTax, 
    hasMathError,
    totalInputCredits,
    b2bCreditTransferred,
    isReformActive
  } = pricing;
  const finalPrice = formData.finalPrice && formData.finalPrice > 0 ? formData.finalPrice : calculatedPrice;
  const effectiveMargin = costPrice > 0 ? Math.max(0, ((finalPrice - costPrice) / costPrice) * 100) : 0;
  const displayMargin = !isFinite(effectiveMargin) ? 0 : effectiveMargin;
  const effectiveProfit = finalPrice - costPrice;

  // Preenche automaticamente o nome do serviço/kit se estiver em branco e houver serviço adicionado
  useEffect(() => {
    if (!formData.serviceName || formData.serviceName.trim() === '') {
      if (formData.services && formData.services.length > 0 && formData.services[0].name) {
        const firstServiceName = formData.services[0].name.trim();
        if (firstServiceName) {
          setFormData(prev => {
            if (!prev.serviceName || prev.serviceName.trim() === '') {
              return { ...prev, serviceName: firstServiceName };
            }
            return prev;
          });
        }
      }
    }
  }, [formData.services, formData.serviceName, setFormData]);

  const loadRecipe = useCallback((recipeId: string) => {
    const recipe = recipes.find(r => r.id === recipeId);
    if (!recipe) return;

    const recipeProducts = (recipe.items || []).map(item => {
      const material = materials.find(m => m.id === item.materialId);
      const yieldAmt = material?.yieldAmount || 1;
      const unitValue = material ? (material.pricePaid / yieldAmt) : 0;
      const cbsCreditProportional = material?.cbsCredit ? (material.cbsCredit / yieldAmt) * item.quantity : 0;
      const ibsCreditProportional = material?.ibsCredit ? (material.ibsCredit / yieldAmt) * item.quantity : 0;
      return {
        name: material?.name || 'Item desconhecido',
        quantity: item.quantity,
        unitValue: unitValue,
        total: unitValue * item.quantity,
        materialId: item.materialId,
        isPassThrough: false,
        isFromRecipe: true,
        showOnPdf: true,
        cbsCredit: Number(cbsCreditProportional.toFixed(2)),
        ibsCredit: Number(ibsCreditProportional.toFixed(2)),
      };
    });

    setFormData(prev => ({
      ...prev,
      serviceName: recipe.name,
      products: recipeProducts,
      timeHours: Math.floor((recipe.executionTimeMinutes || 0) / 60),
      timeMinutes: (recipe.executionTimeMinutes || 0) % 60
    }));

    setAiExplanation(`Kit/Receita "${recipe.name}" carregado com sucesso.`);
    timerRef.current = setTimeout(() => setBudgetReady(true), 500);
  }, [recipes, materials, setFormData, setAiExplanation, setBudgetReady]);

  const buildSnapshotProducts = useCallback((products: QuoteProductItem[]): QuoteProductItem[] => {
    const snapshotAt = new Date().toISOString();
    return products.map(p => {
      let matched = p.materialId ? materials.find(m => m.id === p.materialId) : undefined;

      if (!matched) {
        const nameLower = (p.name || '').toLowerCase();
        matched = materials.find(m => {
          const matName = (m.name || '').toLowerCase();
          return matName.includes(nameLower) || nameLower.includes(matName);
        });
      }

      if (matched) {
        return {
          ...p,
          materialId: matched.id,
          snapshotPrice: matched.pricePaid,
          snapshotAt,
        };
      }
      return p;
    });
  }, [materials]);

  const unitService = (formData.services || []).find(s => s.isUnitBased);
  const totalMinutes = (formData.timeHours * 60) + formData.timeMinutes;
  const vhb = config.baseHourValue || 50;

  const productivityBreakEven: ProductivityBreakEven | null = useMemo(() => {
    if (!unitService || totalMinutes > 0) return null;
    const unitVal = Number(unitService.unitValue || unitService.value || 0);
    if (unitVal <= 0 || vhb <= 0) return null;

    const minItemsPerHour = Math.ceil(vhb / unitVal);
    const maxSecondsPerItem = Math.floor(3600 / minItemsPerHour);
    const minMins = Math.floor(maxSecondsPerItem / 60);
    const remSecs = maxSecondsPerItem % 60;
    const maxTimeFormatted = minMins > 0 ? `${minMins}m${remSecs > 0 ? `${remSecs}s` : ''}` : `${remSecs}s`;

    // Simulação dinâmica parametrizada com ritmo médio de 1 min por item (60 itens/h)
    const pacingPerHour = 60;
    const dynamicHourlyRevenue = Math.round(pacingPerHour * unitVal * 100) / 100;
    const netSpreadPerHour = Math.round((dynamicHourlyRevenue - vhb) * 100) / 100;

    const rawLabel = unitService.unitLabel || 'item';
    const unitLabelPlural = rawLabel === 'item' ? 'itens' : rawLabel === 'un' ? 'unidades' : `${rawLabel}s`;

    return {
      unitVal,
      unitLabel: rawLabel,
      unitLabelPlural,
      vhb,
      minItemsPerHour,
      maxTimeFormatted,
      dynamicHourlyRevenue,
      netSpreadPerHour
    };
  }, [unitService, totalMinutes, vhb]);

  const {
    effectiveClientCostB2B = finalPrice,
    tcuBenchmarkApplied = false,
    tcuProcessCitation
  } = pricing;

  return {
    costPrice,
    finalPrice,
    effectiveMargin,
    effectiveProfit,
    displayMargin,
    hiddenMarkupTotal,
    useOperationalTax,
    hasMathError,
    productivityBreakEven,
    totalInputCredits,
    b2bCreditTransferred,
    effectiveClientCostB2B,
    tcuBenchmarkApplied,
    tcuProcessCitation,
    isReformActive,
    riskAlert,
    loadRecipe,
    buildSnapshotProducts,
    isServiceNicheActive
  };
}
